import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ============================================
// Type Definitions
// ============================================

interface AuthenticationState {
  isLoginSuccess: boolean;
  isLoginPending: boolean;
  loginError: boolean;
}

interface UserState {
  permissions: Record<string, unknown>;
  user: Record<string, unknown>;
  refreshToken: string;
  jwtToken: string;
  UACTypedInInformation: Record<string, unknown>;
  userManagementAccounts: unknown[];
  selectedUserToEdit: {
    user: { userId: number; firstName?: string; lastName?: string; username?: string };
    role: { roleId: number; roleName?: string; requiresMemberId?: boolean };
    memberId?: string;
    affPinNum?: string;
    Active?: boolean;
  };
}

interface RegistrationState {
  isRegistrationSuccess: boolean;
  isRegistrationPending: boolean;
  registrationError: unknown;
}

interface SearchState {
  affiliateSearchRequest: unknown;
  affiliateSearchResult: unknown[];
  affiliateSearchPending: boolean;
  affiliateSearchSuccess: boolean;
  affiliateSearchError: unknown;
  memberSearchRequest: unknown;
  memberSearchResult: unknown[];
  memberSearchPending: boolean;
  memberSearchSuccess: boolean;
  memberSearchError: unknown;
}

interface OnboardingState {
  isOnBoardingRequestSuccessful: boolean;
  isOnBoardingRequestPending: boolean;
  onBoardingError: unknown;
}

interface GroupRegistrationState {
  groupRegistrationData: unknown;
  isGroupRegistrationPending: boolean;
  isGroupRegistrationSuccess: boolean;
  groupRegistrationError: unknown;
  registrantResults: unknown[];
  fileLoading: boolean;
  fileLoaded: boolean;
}

interface CommitteesState {
  committeeAssignments: unknown[];
  committeeTypes: unknown[];
  committeesLoading: boolean;
  committeesError: unknown;
  currentSelectedCommittee: unknown;
}

interface AttendeeState {
  attendeeRecord: unknown;
  loadAttendeePending: boolean;
  saveAttendeePending: boolean;
  attendeeSavePending: boolean;
  attendeeLoadPending: boolean;
  attendeeError: unknown;
}

interface CommunicationsState {
  communications: unknown[];
  communicationsPending: boolean;
  communicationsError: unknown;
  credentialTypes: unknown[];
}

interface StateReducerState {
  states: unknown[];
  statesLoading: boolean;
  statesError: unknown;
}

interface AuditState {
  revisions: unknown[];
  revisionsLoadedSuccessfully: boolean;
  sortingDates: unknown[];
  revisionsForSorting: unknown[];
  auditLoading: boolean;
  auditError: unknown;
  perModuleRevisionContent: {
    moduleRevision: Record<string, unknown>;
  };
  selectedRevisionContent: unknown;
  selectedRevisionContentLoadedSuccessfully: boolean;
  selectedRevisionContentLoadPending: boolean;
  selectedRevisionContentError: unknown;
}

interface AffiliateState {
  selectedAffiliate: unknown;
  selectedAffiliateRequestSuccess: boolean;
  affiliateLoading: boolean;
  affiliateError: unknown;
}

interface GuestInvitationState {
  guestInvitations: unknown[];
  guestInvitationPending: boolean;
  guestInvitationError: unknown;
  memberGuests: unknown[];
}

interface VisitorRegistrationState {
  visitorData: unknown;
  visitorRegistrationPending: boolean;
  visitorRegistrationError: unknown;
  memberVisitors: unknown[];
  isMemberRequestError: unknown;
}

interface HotelReservationsState {
  reservations: unknown[];
  reservationPending: boolean;
  reservationCompleteInitialLoadRequestPending: boolean;
  reservationCompleteInitialLoadRequestError: unknown;
  reservationCompleteInitialLoad: unknown;
  reservationError: unknown;
  editReservationLink: string;
}

interface AttendeeSummaryState {
  attendeeSummaryData: unknown;
  attendeeSummaryPending: boolean;
  attendeeSummaryError: unknown;
}

interface CredentialsState {
  credentialTypes: unknown[];
  credentialsLoading: boolean;
  credentialsError: unknown;
}

interface PermissionType {
  permissionId: number;
  permissionLabel: string;
  permissionExists: boolean;
}

interface SelectedUserType {
  permissionLabel: string;
}

interface RolesAndPermissionsState {
  roles: unknown[];
  rolesLoading: boolean;
  rolesError: unknown;
  selectedRole: { id: number; name: string };
  userTypePermissions: PermissionType[];
  moduleTypePermissions: PermissionType[];
  selectedModuleTypes: PermissionType[];
  loadedUserType: SelectedUserType;
  selectedUserType: SelectedUserType;
}

interface ConventionState {
  activeConvention: { conventionId: number; title?: string; [key: string]: unknown };
  conventions: unknown[];
  isActiveConventionLoading: boolean;
  conventionError: unknown;
}

// ============================================
// Combined Store State
// ============================================

interface AppState {
  // Authentication slice
  authentication: AuthenticationState;
  setLoginPending: () => void;
  setLoginSuccess: () => void;
  setLoginError: () => void;
  resetAuthentication: () => void;

  // User slice
  user: UserState;
  setUserPermissions: (permissions: Record<string, unknown>) => void;
  setUserRefreshToken: (refreshToken: string) => void;
  setUserJwtToken: (jwtToken: string) => void;
  setUser: (user: Record<string, unknown>) => void;
  setSelectedUserToEdit: (selectedUserToEdit: {
    user: { userId: number; firstName?: string; lastName?: string; username?: string };
    role: { roleId: number; roleName?: string; requiresMemberId?: boolean };
    memberId?: string;
    affPinNum?: string;
    Active?: boolean;
  }) => void;
  setUserManagementAccounts: (accounts: unknown[]) => void;
  setUserCreateDetails: (details: Record<string, unknown>) => void;
  clearUserCreateDetails: () => void;
  resetUser: () => void;

  // Registration slice
  registration: RegistrationState;
  setRegistrationPending: () => void;
  setRegistrationSuccess: () => void;
  setRegistrationError: (error: unknown) => void;
  resetRegistration: () => void;

  // Search slice
  search: SearchState;
  setAffiliateSearchPending: () => void;
  setAffiliateSearchSuccess: (results: unknown[]) => void;
  setAffiliateSearchError: (error: unknown) => void;
  setMemberSearchPending: () => void;
  setMemberSearchSuccess: (results: unknown[]) => void;
  setMemberSearchError: (error: unknown) => void;
  setMemberSearchRequest: (request: unknown) => void;
  resetMemberSearch: () => void;

  // Onboarding slice
  onboarding: OnboardingState;
  setOnboardingPending: () => void;
  setOnboardingSuccess: () => void;
  setOnboardingError: (error: unknown) => void;

  // Group Registration slice
  groupRegistration: GroupRegistrationState;
  setGroupRegistrationPending: () => void;
  setGroupRegistrationSuccess: (data: unknown) => void;
  setGroupRegistrationError: (error: unknown) => void;
  setGroupRegistrationSuccessful: (data?: unknown) => void;
  setFileLoading: (loading?: boolean) => void;
  setFileLoaded: (loaded?: boolean) => void;
  setRegistrantResults: (results: unknown[]) => void;

  // Credentials slice (alias for backward compatibility)
  credentials: CredentialsState;

  // Committees slice
  committees: CommitteesState;
  setCommitteeAssignments: (assignments: unknown[]) => void;
  setCommitteeTypes: (types: unknown[]) => void;
  setCommitteesLoading: (loading: boolean) => void;
  setCommitteesError: (error: unknown) => void;
  updateCurrentCommitteeType: (committee: unknown) => void;

  // Attendee slice
  attendee: AttendeeState;
  setAttendeeRecord: (record: unknown) => void;
  setLoadAttendeePending: (pending: boolean) => void;
  setSaveAttendeePending: (pending: boolean) => void;
  setAttendeeError: (error: unknown) => void;
  setSaveAttendeeSuccess: (record: unknown) => void;
  setSaveAttendeeError: (error: unknown) => void;
  setLoadAttendeeSuccess: (record: unknown) => void;
  setLoadAttendeeError: (error: unknown) => void;

  // Communications slice
  communications: CommunicationsState;
  setCommunications: (communications: unknown[]) => void;
  setCommunicationsPending: (pending: boolean) => void;
  setCommunicationsError: (error: unknown) => void;

  // State reducer slice
  stateReducer: StateReducerState;
  setStates: (states: unknown[]) => void;
  setStatesLoading: (loading: boolean) => void;
  setStatesError: (error: unknown) => void;

  // Audit slice
  auditAndHistoryRevisions: AuditState;
  setRevisions: (revisions: unknown[]) => void;
  setRevisionsLoadedSuccessfully: (success: boolean) => void;
  setSortingDates: (dates: unknown[]) => void;
  setRevisionsForSorting: (revisions: unknown[]) => void;
  setAuditLoading: (loading: boolean) => void;
  setAuditError: (error: unknown) => void;
  setPerModuleRevisionContent: (objectType: string, data: unknown) => void;
  setSelectedRevisionContent: (content: unknown) => void;
  setSelectedRevisionContentLoadedSuccessfully: () => void;
  setSelectedRevisionContentLoadedError: (error: unknown) => void;
  setSelectedRevisionContentLoadPending: () => void;

  // Affiliate slice
  affiliate: AffiliateState;
  setSelectedAffiliate: (affiliate: unknown) => void;
  setSelectedAffiliateRequestSuccess: (success: boolean) => void;
  setAffiliateLoading: (loading: boolean) => void;
  setAffiliateError: (error: unknown) => void;
  setSelectedRequestSuccess: () => void;
  setSelectedRequestLoading: () => void;

  // Guest Invitation slice
  guestInvitation: GuestInvitationState;
  setGuestInvitations: (invitations: unknown[]) => void;
  setGuestInvitationPending: (pending: boolean) => void;
  setGuestInvitationError: (error: unknown) => void;
  setMemberGuests: (guests: unknown[]) => void;

  // Visitor Registration slice
  visitorRegistration: VisitorRegistrationState;
  setVisitorData: (data: unknown) => void;
  setVisitorRegistrationPending: (pending: boolean) => void;
  setVisitorRegistrationError: (error: unknown) => void;
  setMemberVisitors: (visitors: unknown[]) => void;
  setIsMemberRequestError: (error: unknown) => void;

  // Hotel Reservations slice
  hotelReservations: HotelReservationsState;
  setReservations: (reservations: unknown[]) => void;
  setReservationPending: (pending?: boolean) => void;
  setReservationCompleteInitialLoad: (data: unknown) => void;
  setReservationError: (error?: unknown) => void;
  setReservationCompleteInitialLoadPending: () => void;
  setReservationCompleteInitialLoadSuccess: (data?: unknown) => void;
  setReservationCompleteInitialLoadError: (error?: unknown) => void;
  setEditReservationLink: (link: string) => void;
  setReservationComplete: (data?: unknown) => void;
  resetPendingRequests: () => void;
  resetHotelState: () => void;

  // Attendee Summary slice
  attendeeSummary: AttendeeSummaryState;
  setAttendeeSummaryData: (data: unknown) => void;
  setAttendeeSummaryPending: (pending: boolean) => void;
  setAttendeeSummaryError: (error: unknown) => void;

  // Credentials slice
  credentialsReducer: CredentialsState;
  setCredentialTypes: (types: unknown[]) => void;
  setCredentialsLoading: (loading: boolean) => void;
  setCredentialsError: (error: unknown) => void;

  // Roles and Permissions slice
  rolesAndPermissions: RolesAndPermissionsState;
  setRoles: (roles: unknown[]) => void;
  setRolesLoading: (loading: boolean) => void;
  setRolesError: (error: unknown) => void;
  setSelectedRole: (role: { id: number; name: string }) => void;
  setUserTypePermissions: (permissions: PermissionType[]) => void;
  setModuleTypePermissions: (permissions: PermissionType[]) => void;
  setSelectedUserType: (userType: SelectedUserType) => void;
  setSelectedModuleTypes: (moduleTypes: PermissionType[]) => void;
  setLoadedUserType: (userType: SelectedUserType) => void;
  resetDataBoundPermissions: () => void;

  // Convention slice
  conventionReducer: ConventionState;
  convention: ConventionState; // Alias for backwards compatibility
  setActiveConvention: (convention: unknown) => void;
  setConventions: (conventions: unknown[]) => void;
  setIsActiveConventionLoading: (loading: boolean) => void;
  setConventionError: (error: unknown) => void;

  // Global reset
  resetStore: () => void;
}

// ============================================
// Initial States
// ============================================

const initialAuthenticationState: AuthenticationState = {
  isLoginSuccess: false,
  isLoginPending: false,
  loginError: false,
};

const initialUserState: UserState = {
  permissions: {},
  user: {},
  refreshToken: '',
  jwtToken: '',
  UACTypedInInformation: {},
  userManagementAccounts: [],
  selectedUserToEdit: {
    user: { userId: 0 },
    role: { roleId: 1, requiresMemberId: false },
  },
};

const initialRegistrationState: RegistrationState = {
  isRegistrationSuccess: false,
  isRegistrationPending: false,
  registrationError: null,
};

const initialSearchState: SearchState = {
  affiliateSearchRequest: null,
  affiliateSearchResult: [],
  affiliateSearchPending: false,
  affiliateSearchSuccess: false,
  affiliateSearchError: null,
  memberSearchRequest: null,
  memberSearchResult: [],
  memberSearchPending: false,
  memberSearchSuccess: false,
  memberSearchError: null,
};

const initialOnboardingState: OnboardingState = {
  isOnBoardingRequestSuccessful: false,
  isOnBoardingRequestPending: false,
  onBoardingError: null,
};

const initialGroupRegistrationState: GroupRegistrationState = {
  groupRegistrationData: null,
  isGroupRegistrationPending: false,
  isGroupRegistrationSuccess: false,
  groupRegistrationError: null,
  registrantResults: [],
  fileLoading: false,
  fileLoaded: false,
};

const initialCommitteesState: CommitteesState = {
  committeeAssignments: [],
  committeeTypes: [],
  committeesLoading: false,
  committeesError: null,
  currentSelectedCommittee: null,
};

const initialAttendeeState: AttendeeState = {
  attendeeRecord: null,
  loadAttendeePending: false,
  saveAttendeePending: false,
  attendeeSavePending: false,
  attendeeLoadPending: false,
  attendeeError: null,
};

const initialCommunicationsState: CommunicationsState = {
  communications: [],
  communicationsPending: false,
  communicationsError: null,
  credentialTypes: [],
};

const initialStateReducerState: StateReducerState = {
  states: [],
  statesLoading: false,
  statesError: null,
};

const initialAuditState: AuditState = {
  revisions: [],
  revisionsLoadedSuccessfully: false,
  sortingDates: [],
  revisionsForSorting: [],
  auditLoading: false,
  auditError: null,
  perModuleRevisionContent: {
    moduleRevision: {},
  },
  selectedRevisionContent: null,
  selectedRevisionContentLoadedSuccessfully: false,
  selectedRevisionContentLoadPending: false,
  selectedRevisionContentError: null,
};

const initialAffiliateState: AffiliateState = {
  selectedAffiliate: null,
  selectedAffiliateRequestSuccess: false,
  affiliateLoading: false,
  affiliateError: null,
};

const initialGuestInvitationState: GuestInvitationState = {
  guestInvitations: [],
  guestInvitationPending: false,
  guestInvitationError: null,
  memberGuests: [],
};

const initialVisitorRegistrationState: VisitorRegistrationState = {
  visitorData: null,
  visitorRegistrationPending: false,
  visitorRegistrationError: null,
  memberVisitors: [],
  isMemberRequestError: null,
};

const initialHotelReservationsState: HotelReservationsState = {
  reservations: [],
  reservationPending: false,
  reservationCompleteInitialLoadRequestPending: false,
  reservationCompleteInitialLoadRequestError: null,
  reservationCompleteInitialLoad: null,
  reservationError: null,
  editReservationLink: '',
};

const initialAttendeeSummaryState: AttendeeSummaryState = {
  attendeeSummaryData: null,
  attendeeSummaryPending: false,
  attendeeSummaryError: null,
};

const initialCredentialsState: CredentialsState = {
  credentialTypes: [],
  credentialsLoading: false,
  credentialsError: null,
};

const initialRolesAndPermissionsState: RolesAndPermissionsState = {
  roles: [],
  rolesLoading: false,
  rolesError: null,
  selectedRole: { id: 0, name: '' },
  userTypePermissions: [],
  moduleTypePermissions: [],
  selectedModuleTypes: [],
  loadedUserType: { permissionLabel: '' },
  selectedUserType: { permissionLabel: '' },
};

const initialConventionState: ConventionState = {
  activeConvention: { conventionId: 0 },
  conventions: [],
  isActiveConventionLoading: false,
  conventionError: null,
};

// ============================================
// Store Creation
// ============================================

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Authentication
      authentication: initialAuthenticationState,
      setLoginPending: () =>
        set((state) => ({
          authentication: {
            ...state.authentication,
            isLoginPending: true,
            isLoginSuccess: false,
          },
        })),
      setLoginSuccess: () =>
        set((state) => ({
          authentication: {
            ...state.authentication,
            isLoginSuccess: true,
            isLoginPending: false,
          },
        })),
      setLoginError: () =>
        set((state) => ({
          authentication: {
            ...state.authentication,
            loginError: true,
            isLoginSuccess: false,
            isLoginPending: false,
          },
        })),
      resetAuthentication: () =>
        set({ authentication: initialAuthenticationState }),

      // User
      user: initialUserState,
      setUserPermissions: (permissions) =>
        set((state) => ({
          user: { ...state.user, permissions },
        })),
      setUserRefreshToken: (refreshToken) =>
        set((state) => ({
          user: { ...state.user, refreshToken },
        })),
      setUserJwtToken: (jwtToken) =>
        set((state) => ({
          user: { ...state.user, jwtToken },
        })),
      setUser: (user) =>
        set((state) => ({
          user: { ...state.user, user },
        })),
      setSelectedUserToEdit: (selectedUserToEdit) =>
        set((state) => ({
          user: { ...state.user, selectedUserToEdit },
        })),
      setUserManagementAccounts: (userManagementAccounts) =>
        set((state) => ({
          user: { ...state.user, userManagementAccounts },
        })),
      setUserCreateDetails: (UACTypedInInformation) =>
        set((state) => ({
          user: { ...state.user, UACTypedInInformation },
        })),
      clearUserCreateDetails: () =>
        set((state) => ({
          user: { ...state.user, UACTypedInInformation: {} },
        })),
      resetUser: () => set({ user: initialUserState }),

      // Registration
      registration: initialRegistrationState,
      setRegistrationPending: () =>
        set((state) => ({
          registration: {
            ...state.registration,
            isRegistrationPending: true,
            isRegistrationSuccess: false,
          },
        })),
      setRegistrationSuccess: () =>
        set((state) => ({
          registration: {
            ...state.registration,
            isRegistrationSuccess: true,
            isRegistrationPending: false,
          },
        })),
      setRegistrationError: (registrationError) =>
        set((state) => ({
          registration: {
            ...state.registration,
            registrationError,
            isRegistrationPending: false,
          },
        })),
      resetRegistration: () => set({ registration: initialRegistrationState }),

      // Search
      search: initialSearchState,
      setAffiliateSearchPending: () =>
        set((state) => ({
          search: {
            ...state.search,
            affiliateSearchResult: [],
            affiliateSearchPending: true,
            affiliateSearchSuccess: false,
            affiliateSearchError: null,
          },
        })),
      setAffiliateSearchSuccess: (affiliateSearchResult) =>
        set((state) => ({
          search: {
            ...state.search,
            affiliateSearchResult,
            affiliateSearchSuccess: true,
            affiliateSearchPending: false,
            affiliateSearchError: null,
          },
        })),
      setAffiliateSearchError: (affiliateSearchError) =>
        set((state) => ({
          search: {
            ...state.search,
            affiliateSearchResult: [],
            affiliateSearchSuccess: false,
            affiliateSearchPending: false,
            affiliateSearchError,
          },
        })),
      setMemberSearchPending: () =>
        set((state) => ({
          search: {
            ...state.search,
            memberSearchResult: [],
            memberSearchSuccess: false,
            memberSearchPending: true,
            memberSearchError: null,
          },
        })),
      setMemberSearchSuccess: (memberSearchResult) =>
        set((state) => ({
          search: {
            ...state.search,
            memberSearchResult,
            memberSearchSuccess: true,
            memberSearchPending: false,
            memberSearchError: null,
          },
        })),
      setMemberSearchError: (memberSearchError) =>
        set((state) => ({
          search: {
            ...state.search,
            memberSearchResult: [],
            memberSearchSuccess: false,
            memberSearchPending: false,
            memberSearchError,
          },
        })),
      setMemberSearchRequest: (memberSearchRequest) =>
        set((state) => ({
          search: { ...state.search, memberSearchRequest },
        })),
      resetMemberSearch: () =>
        set((state) => ({
          search: {
            ...state.search,
            memberSearchRequest: null,
            memberSearchResult: [],
            memberSearchSuccess: false,
            memberSearchPending: false,
            memberSearchError: null,
          },
        })),

      // Onboarding
      onboarding: initialOnboardingState,
      setOnboardingPending: () =>
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            isOnBoardingRequestPending: true,
            isOnBoardingRequestSuccessful: false,
          },
        })),
      setOnboardingSuccess: () =>
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            isOnBoardingRequestSuccessful: true,
            isOnBoardingRequestPending: false,
          },
        })),
      setOnboardingError: (onBoardingError) =>
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            onBoardingError,
            isOnBoardingRequestPending: false,
          },
        })),

      // Group Registration
      groupRegistration: initialGroupRegistrationState,
      setGroupRegistrationPending: () =>
        set((state) => ({
          groupRegistration: {
            ...state.groupRegistration,
            isGroupRegistrationPending: true,
            isGroupRegistrationSuccess: false,
          },
        })),
      setGroupRegistrationSuccess: (groupRegistrationData) =>
        set((state) => ({
          groupRegistration: {
            ...state.groupRegistration,
            groupRegistrationData,
            isGroupRegistrationSuccess: true,
            isGroupRegistrationPending: false,
          },
        })),
      setGroupRegistrationError: (groupRegistrationError) =>
        set((state) => ({
          groupRegistration: {
            ...state.groupRegistration,
            groupRegistrationError,
            isGroupRegistrationPending: false,
          },
        })),
      setGroupRegistrationSuccessful: (groupRegistrationData) =>
        set((state) => ({
          groupRegistration: {
            ...state.groupRegistration,
            groupRegistrationData,
            isGroupRegistrationSuccess: true,
            isGroupRegistrationPending: false,
          },
        })),
      setFileLoading: (fileLoading) =>
        set((state) => ({
          groupRegistration: { ...state.groupRegistration, fileLoading: fileLoading ?? false },
        })),
      setFileLoaded: (fileLoaded) =>
        set((state) => ({
          groupRegistration: { ...state.groupRegistration, fileLoaded: fileLoaded ?? false },
        })),
      setRegistrantResults: (registrantResults) =>
        set((state) => ({
          groupRegistration: { ...state.groupRegistration, registrantResults },
        })),

      // Credentials alias (same as credentialsReducer)
      credentials: initialCredentialsState,

      // Committees
      committees: initialCommitteesState,
      setCommitteeAssignments: (committeeAssignments) =>
        set((state) => ({
          committees: { ...state.committees, committeeAssignments },
        })),
      setCommitteeTypes: (committeeTypes) =>
        set((state) => ({
          committees: { ...state.committees, committeeTypes },
        })),
      setCommitteesLoading: (committeesLoading) =>
        set((state) => ({
          committees: { ...state.committees, committeesLoading },
        })),
      setCommitteesError: (committeesError) =>
        set((state) => ({
          committees: { ...state.committees, committeesError },
        })),
      updateCurrentCommitteeType: (currentSelectedCommittee) =>
        set((state) => ({
          committees: { ...state.committees, currentSelectedCommittee },
        })),

      // Attendee
      attendee: initialAttendeeState,
      setAttendeeRecord: (attendeeRecord) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeRecord },
        })),
      setLoadAttendeePending: (loadAttendeePending) =>
        set((state) => ({
          attendee: { ...state.attendee, loadAttendeePending },
        })),
      setSaveAttendeePending: (saveAttendeePending) =>
        set((state) => ({
          attendee: { ...state.attendee, saveAttendeePending },
        })),
      setAttendeeError: (attendeeError) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeError },
        })),
      setSaveAttendeeSuccess: (attendeeRecord) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeRecord, attendeeSavePending: false, saveAttendeePending: false },
        })),
      setSaveAttendeeError: (attendeeError) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeError, attendeeSavePending: false, saveAttendeePending: false },
        })),
      setLoadAttendeeSuccess: (attendeeRecord) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeRecord, attendeeLoadPending: false, loadAttendeePending: false },
        })),
      setLoadAttendeeError: (attendeeError) =>
        set((state) => ({
          attendee: { ...state.attendee, attendeeError, attendeeLoadPending: false, loadAttendeePending: false },
        })),

      // Communications
      communications: initialCommunicationsState,
      setCommunications: (communications) =>
        set((state) => ({
          communications: {
            ...state.communications,
            communications,
          },
        })),
      setCommunicationsPending: (communicationsPending) =>
        set((state) => ({
          communications: { ...state.communications, communicationsPending },
        })),
      setCommunicationsError: (communicationsError) =>
        set((state) => ({
          communications: { ...state.communications, communicationsError },
        })),

      // State Reducer
      stateReducer: initialStateReducerState,
      setStates: (states) =>
        set((state) => ({
          stateReducer: { ...state.stateReducer, states },
        })),
      setStatesLoading: (statesLoading) =>
        set((state) => ({
          stateReducer: { ...state.stateReducer, statesLoading },
        })),
      setStatesError: (statesError) =>
        set((state) => ({
          stateReducer: { ...state.stateReducer, statesError },
        })),

      // Audit
      auditAndHistoryRevisions: initialAuditState,
      setRevisions: (revisions) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            revisions,
          },
        })),
      setRevisionsLoadedSuccessfully: (revisionsLoadedSuccessfully) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            revisionsLoadedSuccessfully,
          },
        })),
      setSortingDates: (sortingDates) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            sortingDates,
          },
        })),
      setRevisionsForSorting: (revisionsForSorting) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            revisionsForSorting,
          },
        })),
      setAuditLoading: (auditLoading) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            auditLoading,
          },
        })),
      setAuditError: (auditError) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            auditError,
          },
        })),
      setPerModuleRevisionContent: (objectType, data) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            perModuleRevisionContent: {
              ...state.auditAndHistoryRevisions.perModuleRevisionContent,
              moduleRevision: {
                ...state.auditAndHistoryRevisions.perModuleRevisionContent.moduleRevision,
                [objectType]: data,
              },
            },
          },
        })),
      setSelectedRevisionContent: (selectedRevisionContent) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            selectedRevisionContent,
          },
        })),
      setSelectedRevisionContentLoadedSuccessfully: () =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            selectedRevisionContentLoadedSuccessfully: true,
            selectedRevisionContentLoadPending: false,
          },
        })),
      setSelectedRevisionContentLoadedError: (selectedRevisionContentError) =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            selectedRevisionContentError,
            selectedRevisionContentLoadPending: false,
          },
        })),
      setSelectedRevisionContentLoadPending: () =>
        set((state) => ({
          auditAndHistoryRevisions: {
            ...state.auditAndHistoryRevisions,
            selectedRevisionContentLoadPending: true,
            selectedRevisionContentLoadedSuccessfully: false,
          },
        })),

      // Affiliate
      affiliate: initialAffiliateState,
      setSelectedAffiliate: (selectedAffiliate) =>
        set((state) => ({
          affiliate: { ...state.affiliate, selectedAffiliate },
        })),
      setSelectedAffiliateRequestSuccess: (selectedAffiliateRequestSuccess) =>
        set((state) => ({
          affiliate: { ...state.affiliate, selectedAffiliateRequestSuccess },
        })),
      setAffiliateLoading: (affiliateLoading) =>
        set((state) => ({
          affiliate: { ...state.affiliate, affiliateLoading },
        })),
      setAffiliateError: (affiliateError) =>
        set((state) => ({
          affiliate: { ...state.affiliate, affiliateError },
        })),
      setSelectedRequestSuccess: () =>
        set((state) => ({
          affiliate: {
            ...state.affiliate,
            selectedAffiliateRequestSuccess: true,
            affiliateLoading: false,
          },
        })),
      setSelectedRequestLoading: () =>
        set((state) => ({
          affiliate: {
            ...state.affiliate,
            affiliateLoading: true,
            selectedAffiliateRequestSuccess: false,
          },
        })),

      // Guest Invitation
      guestInvitation: initialGuestInvitationState,
      setGuestInvitations: (guestInvitations) =>
        set((state) => ({
          guestInvitation: { ...state.guestInvitation, guestInvitations },
        })),
      setGuestInvitationPending: (guestInvitationPending) =>
        set((state) => ({
          guestInvitation: { ...state.guestInvitation, guestInvitationPending },
        })),
      setGuestInvitationError: (guestInvitationError) =>
        set((state) => ({
          guestInvitation: { ...state.guestInvitation, guestInvitationError },
        })),
      setMemberGuests: (memberGuests) =>
        set((state) => ({
          guestInvitation: { ...state.guestInvitation, memberGuests },
        })),

      // Visitor Registration
      visitorRegistration: initialVisitorRegistrationState,
      setVisitorData: (visitorData) =>
        set((state) => ({
          visitorRegistration: { ...state.visitorRegistration, visitorData },
        })),
      setVisitorRegistrationPending: (visitorRegistrationPending) =>
        set((state) => ({
          visitorRegistration: {
            ...state.visitorRegistration,
            visitorRegistrationPending,
          },
        })),
      setVisitorRegistrationError: (visitorRegistrationError) =>
        set((state) => ({
          visitorRegistration: {
            ...state.visitorRegistration,
            visitorRegistrationError,
          },
        })),
      setMemberVisitors: (memberVisitors) =>
        set((state) => ({
          visitorRegistration: { ...state.visitorRegistration, memberVisitors },
        })),
      setIsMemberRequestError: (isMemberRequestError) =>
        set((state) => ({
          visitorRegistration: { ...state.visitorRegistration, isMemberRequestError },
        })),

      // Hotel Reservations
      hotelReservations: initialHotelReservationsState,
      setReservations: (reservations) =>
        set((state) => ({
          hotelReservations: { ...state.hotelReservations, reservations },
        })),
      setReservationPending: (reservationPending) =>
        set((state) => ({
          hotelReservations: { ...state.hotelReservations, reservationPending: reservationPending ?? false },
        })),
      setReservationCompleteInitialLoad: (reservationCompleteInitialLoad) =>
        set((state) => ({
          hotelReservations: {
            ...state.hotelReservations,
            reservationCompleteInitialLoad,
          },
        })),
      setReservationError: (reservationError) =>
        set((state) => ({
          hotelReservations: { ...state.hotelReservations, reservationError },
        })),
      setReservationCompleteInitialLoadPending: () =>
        set((state) => ({
          hotelReservations: {
            ...state.hotelReservations,
            reservationCompleteInitialLoadRequestPending: true,
            reservationCompleteInitialLoadRequestError: null,
          },
        })),
      setReservationCompleteInitialLoadSuccess: (reservationCompleteInitialLoad) =>
        set((state) => ({
          hotelReservations: {
            ...state.hotelReservations,
            reservationCompleteInitialLoad,
            reservationCompleteInitialLoadRequestPending: false,
          },
        })),
      setReservationCompleteInitialLoadError: (reservationCompleteInitialLoadRequestError) =>
        set((state) => ({
          hotelReservations: {
            ...state.hotelReservations,
            reservationCompleteInitialLoadRequestError,
            reservationCompleteInitialLoadRequestPending: false,
          },
        })),
      setEditReservationLink: (editReservationLink) =>
        set((state) => ({
          hotelReservations: { ...state.hotelReservations, editReservationLink },
        })),
      setReservationComplete: (reservationCompleteInitialLoad) =>
        set((state) => ({
          hotelReservations: { ...state.hotelReservations, reservationCompleteInitialLoad },
        })),
      resetPendingRequests: () =>
        set((state) => ({
          hotelReservations: {
            ...state.hotelReservations,
            reservationPending: false,
            reservationCompleteInitialLoadRequestPending: false,
          },
        })),
      resetHotelState: () =>
        set({ hotelReservations: initialHotelReservationsState }),

      // Attendee Summary
      attendeeSummary: initialAttendeeSummaryState,
      setAttendeeSummaryData: (attendeeSummaryData) =>
        set((state) => ({
          attendeeSummary: { ...state.attendeeSummary, attendeeSummaryData },
        })),
      setAttendeeSummaryPending: (attendeeSummaryPending) =>
        set((state) => ({
          attendeeSummary: { ...state.attendeeSummary, attendeeSummaryPending },
        })),
      setAttendeeSummaryError: (attendeeSummaryError) =>
        set((state) => ({
          attendeeSummary: { ...state.attendeeSummary, attendeeSummaryError },
        })),

      // Credentials
      credentialsReducer: initialCredentialsState,
      setCredentialTypes: (credentialTypes) =>
        set((state) => ({
          credentialsReducer: { ...state.credentialsReducer, credentialTypes },
          credentials: { ...state.credentials, credentialTypes },
        })),
      setCredentialsLoading: (credentialsLoading) =>
        set((state) => ({
          credentialsReducer: { ...state.credentialsReducer, credentialsLoading },
        })),
      setCredentialsError: (credentialsError) =>
        set((state) => ({
          credentialsReducer: { ...state.credentialsReducer, credentialsError },
        })),

      // Roles and Permissions
      rolesAndPermissions: initialRolesAndPermissionsState,
      setRoles: (roles) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, roles },
        })),
      setRolesLoading: (rolesLoading) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, rolesLoading },
        })),
      setRolesError: (rolesError) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, rolesError },
        })),
      setSelectedRole: (selectedRole) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, selectedRole },
        })),
      setUserTypePermissions: (userTypePermissions) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, userTypePermissions },
        })),
      setModuleTypePermissions: (moduleTypePermissions) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, moduleTypePermissions },
        })),
      setSelectedUserType: (selectedUserType) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, selectedUserType },
        })),
      setSelectedModuleTypes: (selectedModuleTypes) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, selectedModuleTypes },
        })),
      setLoadedUserType: (loadedUserType) =>
        set((state) => ({
          rolesAndPermissions: { ...state.rolesAndPermissions, loadedUserType },
        })),
      resetDataBoundPermissions: () =>
        set((state) => ({
          rolesAndPermissions: {
            ...state.rolesAndPermissions,
            userTypePermissions: [],
            moduleTypePermissions: [],
            selectedModuleTypes: [],
            loadedUserType: { permissionLabel: '' },
            selectedUserType: { permissionLabel: '' },
          },
        })),

      // Convention
      conventionReducer: initialConventionState,
      convention: initialConventionState, // Alias for backwards compatibility
      setActiveConvention: (activeConvention) =>
        set((state) => ({
          conventionReducer: { ...state.conventionReducer, activeConvention: activeConvention as { conventionId: number; [key: string]: unknown } },
          convention: { ...state.convention, activeConvention: activeConvention as { conventionId: number; [key: string]: unknown } },
        })),
      setConventions: (conventions) =>
        set((state) => ({
          conventionReducer: { ...state.conventionReducer, conventions },
        })),
      setIsActiveConventionLoading: (isActiveConventionLoading) =>
        set((state) => ({
          conventionReducer: {
            ...state.conventionReducer,
            isActiveConventionLoading,
          },
        })),
      setConventionError: (conventionError) =>
        set((state) => ({
          conventionReducer: { ...state.conventionReducer, conventionError },
        })),

      // Global reset
      resetStore: () =>
        set({
          authentication: initialAuthenticationState,
          user: initialUserState,
          registration: initialRegistrationState,
          search: initialSearchState,
          onboarding: initialOnboardingState,
          groupRegistration: initialGroupRegistrationState,
          committees: initialCommitteesState,
          attendee: initialAttendeeState,
          communications: initialCommunicationsState,
          stateReducer: initialStateReducerState,
          auditAndHistoryRevisions: initialAuditState,
          affiliate: initialAffiliateState,
          guestInvitation: initialGuestInvitationState,
          visitorRegistration: initialVisitorRegistrationState,
          hotelReservations: initialHotelReservationsState,
          attendeeSummary: initialAttendeeSummaryState,
          credentialsReducer: initialCredentialsState,
          credentials: initialCredentialsState,
          rolesAndPermissions: initialRolesAndPermissionsState,
          conventionReducer: initialConventionState,
          convention: initialConventionState,
        }),
    }),
    {
      name: 'aft-cms-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        // Only persist certain slices (matching original Redux blacklist behavior)
        authentication: state.authentication,
        user: state.user,
        registration: state.registration,
        onboarding: state.onboarding,
        groupRegistration: state.groupRegistration,
        committees: state.committees,
        attendee: state.attendee,
        communications: state.communications,
        stateReducer: state.stateReducer,
        affiliate: state.affiliate,
        guestInvitation: state.guestInvitation,
        visitorRegistration: state.visitorRegistration,
        attendeeSummary: state.attendeeSummary,
        credentialsReducer: state.credentialsReducer,
        rolesAndPermissions: state.rolesAndPermissions,
        conventionReducer: state.conventionReducer,
        // Excluded (matching original blacklist): search, hotelReservations, auditAndHistoryRevisions
      }),
    }
  )
);

// ============================================
// Helper Hooks for Backward Compatibility
// ============================================

// These hooks provide a similar API to the old useSelector pattern
export const useAuthentication = () => useAppStore((state) => state.authentication);
export const useUser = () => useAppStore((state) => state.user);
export const useRegistration = () => useAppStore((state) => state.registration);
export const useSearch = () => useAppStore((state) => state.search);
export const useOnboarding = () => useAppStore((state) => state.onboarding);
export const useGroupRegistration = () => useAppStore((state) => state.groupRegistration);
export const useCommittees = () => useAppStore((state) => state.committees);
export const useAttendee = () => useAppStore((state) => state.attendee);
export const useCommunications = () => useAppStore((state) => state.communications);
export const useStateReducer = () => useAppStore((state) => state.stateReducer);
export const useAuditAndHistoryRevisions = () => useAppStore((state) => state.auditAndHistoryRevisions);
export const useAffiliate = () => useAppStore((state) => state.affiliate);
export const useGuestInvitation = () => useAppStore((state) => state.guestInvitation);
export const useVisitorRegistration = () => useAppStore((state) => state.visitorRegistration);
export const useHotelReservations = () => useAppStore((state) => state.hotelReservations);
export const useAttendeeSummary = () => useAppStore((state) => state.attendeeSummary);
export const useCredentialsReducer = () => useAppStore((state) => state.credentialsReducer);
export const useRolesAndPermissions = () => useAppStore((state) => state.rolesAndPermissions);
export const useConventionReducer = () => useAppStore((state) => state.conventionReducer);
